import { useEffect, useState, useCallback } from 'react';

const THEME_KEY  = 'rl_dashboard_theme';
const ACCENT_KEY = 'rl_dashboard_accent';
const AUTO_KEY   = 'rl_dashboard_auto_daynight';

/**
 * Maps every theme ID to its underlying React variant (glass/light/dark).
 * The new 5 themes reuse an existing variant + apply a CSS overlay
 * (see /styles/dashboard-themes.css) via the html[data-rl-theme=...] attr.
 */
export const THEME_BASE_MAP = {
  glass:         'glass',
  light:         'light',
  dark:          'dark',
  'paper-white': 'light',  // 2026-07-11 restore: user still uses this theme (pure white bg)
};

// 2026-07-11: Users may have exotic themes (corp-navy, sunset, amoled, cyber-neon,
// forest, solarized) still saved in localStorage. Auto-migrate them back to 'glass'.
const _sanitizeTheme = (t) => (t && THEME_BASE_MAP[t]) ? t : 'glass';

const isDayTime = () => {
  const h = new Date().getHours();
  return h >= 6 && h < 18;
};

export default function useDashboardTheme() {
  const [rawTheme, setRawTheme] = useState(() => _sanitizeTheme(localStorage.getItem(THEME_KEY)));
  const [accent, setAccentState] = useState(() => localStorage.getItem(ACCENT_KEY) || '#4f46e5');
  const [autoDayNight, setAutoDayNight] = useState(() => {
    // 2026-07-11 Default TRUE for new users (siang -> light, malam -> dark).
    // Respect explicit opt-out ('0') that user saved before.
    const v = localStorage.getItem(AUTO_KEY);
    return v === null ? true : v === '1';
  });

  // Effective theme considering Auto Day/Night mode.
  const effectiveTheme = autoDayNight ? (isDayTime() ? 'light' : 'dark') : rawTheme;
  const baseVariant = THEME_BASE_MAP[effectiveTheme] || 'glass';

  // Sync to localStorage + DOM
  useEffect(() => {
    localStorage.setItem(THEME_KEY, rawTheme);
  }, [rawTheme]);

  useEffect(() => {
    localStorage.setItem(ACCENT_KEY, accent);
    document.documentElement.style.setProperty('--rl-accent', accent);
    // Derive soft + strong from accent
    document.documentElement.style.setProperty('--rl-accent-soft', accent + '22');
  }, [accent]);

  useEffect(() => {
    localStorage.setItem(AUTO_KEY, autoDayNight ? '1' : '0');
  }, [autoDayNight]);

  useEffect(() => {
    document.documentElement.setAttribute('data-rl-theme', effectiveTheme);
    // Sync Tailwind's dark class so dark: variants activate for dark-variant themes
    // (dark, cyber-neon, amoled). Uses baseVariant mapping from THEME_BASE_MAP.
    if (baseVariant === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [effectiveTheme, baseVariant]);

  // Auto day/night re-check every minute
  useEffect(() => {
    if (!autoDayNight) return;
    const id = setInterval(() => {
      // Force re-render via state poke
      setAutoDayNight(prev => prev);  // no-op state set still re-renders parents that read effectiveTheme
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [autoDayNight]);

  // Cross-component sync via custom event (e.g. Layout menu)
  useEffect(() => {
    const handler = (e) => {
      const detail = (e && e.detail) || {};
      if (detail.theme !== undefined) setRawTheme(detail.theme);
      if (detail.accent !== undefined) setAccentState(detail.accent);
      if (detail.autoDayNight !== undefined) setAutoDayNight(!!detail.autoDayNight);
    };
    window.addEventListener('rl-theme-change', handler);
    return () => window.removeEventListener('rl-theme-change', handler);
  }, []);

  const setTheme = useCallback((id) => {
    setRawTheme(id);
    window.dispatchEvent(new CustomEvent('rl-theme-change', { detail: { theme: id } }));
  }, []);
  const setAccent = useCallback((c) => {
    setAccentState(c);
    window.dispatchEvent(new CustomEvent('rl-theme-change', { detail: { accent: c } }));
  }, []);
  const setAutoDayNightFn = useCallback((v) => {
    setAutoDayNight(v);
    window.dispatchEvent(new CustomEvent('rl-theme-change', { detail: { autoDayNight: v } }));
  }, []);

  return {
    theme: rawTheme,
    effectiveTheme,
    baseVariant,
    accent,
    autoDayNight,
    setTheme,
    setAccent,
    setAutoDayNight: setAutoDayNightFn,
  };
}
